<template>
  <main>
    <div v-if="showSearch" class="my-2 container">
      <vf-search-bar
        ref="searchBarRef"
        v-model="searchValue"
        v-style="brandStyles?.searchBar"
        class="m-a"
        @submit="submit"
      />
    </div>
    <!-- Skeleton -->
    <template v-if="pending && (!data || !articlesData?.numFound)">
      <div class="f-col container children:skeleton">
        <div  />
        <div  />
      </div>
    </template>
    <template v-else>
      <cms-section
        v-if="eSpotAboveGrid"
        :key="resultsCount"
        :class="brandClasses.eSpotAboveGrid"
        :section="eSpotAboveGrid"
      />
      <div :class="brandClasses.header">
        <h1 v-if="isArticlesPage" aria-live="polite" :class="brandClasses.title">
          <base-interpolator :content="articlesTitle">
            <template #query="{ args: [message] }">
              <span :class="brandClasses.titleQuery">
                {{ message }}
              </span>
            </template>
          </base-interpolator>
        </h1>
        <template v-else>
          <vf-notification v-if="error" class="mb-4" :dismissible="false" type="error">
            {{ error.message }}
          </vf-notification>
          <div class="flex gap-4">
            <div class="grow">
              <h1 v-if="!hideTitle" :class="brandClasses.title">
                <base-interpolator :content="productsTitle">
                  <template #query="{ args: [message] }">
                    <span :class="brandClasses.titleQuery">
                      {{ message }}
                    </span>
                  </template>
                </base-interpolator>
              </h1>
              <p v-if="autoCorrectMessage" :class="brandClasses.noResultsMessage">
                <base-interpolator
                  :content="[
                    autoCorrectMessage,
                    !data?.autoCorrectQuery && didYouMeanMessage,
                  ].filter(Boolean).join('. ')"
                >
                  <template #query>
                    <span :class="brandClasses.noResultsMessageQuery">
                      {{ data?.autoCorrectQuery }}
                    </span>
                  </template>
                  <template #initialQuery>
                    <span :class="brandClasses.noResultsMessageInitialQuery">
                      {{ query.q }}
                    </span>
                  </template>
                  <template #links>
                    <template v-for="(q, i) in didYouMeanSuggestions" :key="i">
                      <vf-link :class="brandClasses.noResultsMessageLinks" :to="`/search?q=${q}`">
                        {{ q }}
                      </vf-link>
                      <span v-if="didYouMeanSuggestions?.length && i < didYouMeanSuggestions.length - 1">
                        {{ ', ' }}
                      </span>
                    </template>
                  </template>
                </base-interpolator>
              </p>
            </div>
            <p aria-live="polite" :class="brandClasses.productCount">
              {{ data?.total || 0 }} {{ $pluralize($t.products, data?.total || 0) }}
            </p>
          </div>
          <div v-if="data?.campaign" v-html="data.campaign.htmlText" />
        </template>
      </div>
    </template>
    <div v-if="$feature.showArticlesTabOnSRP && pending" class="mb-4 flex justify-center container space-x-3">
      <div class="h-6 w-29 skeleton" />
      <div class="h-6 w-29 skeleton" />
    </div>
    <!-- TODO: maybe inline styles need to be configuratable? -->
    <div
      v-else-if="$feature.showArticlesTabOnSRP && data?.products.length"
      class="flex justify-center gap-6 container"
      :class="tabsBrandClasses.primary.triggerContainer"
      style="border-color: transparent; margin-bottom: 0"
    >
      <base-link
        :class="tabsBrandClasses.primary.trigger"
        :exact-active-class="tabsBrandClasses.primary.triggerActive"
        :to="{ path: '/search', query: { q: searchQuery } }"
      >
        {{ $t.products.other }}
        <template v-if="data?.total !== undefined">
          ({{ data.total }})
        </template>
      </base-link>
      <base-link
        :class="tabsBrandClasses.primary.trigger"
        :exact-active-class="tabsBrandClasses.primary.triggerActive"
        :to="{ path: '/search/articles', query: { q: searchQuery } }"
      >
        {{ $t.articles.other }}
        <template v-if="articlesData?.numFound !== undefined">
          ({{ articlesData.numFound }})
        </template>
      </base-link>
    </div>
    <div v-if="$feature.showFitFinderOnSRP" class="container">
      <div id="fit-finder-srp" />
    </div>
    <NuxtPage
      v-bind="{
        infinityLoad,
        pending,
        searchQuery: data ? (searchQueryAdjusted || query.q) : undefined,
        sideFilters: $feature.showSideFiltersOnSRP,
        trackingCategory,
      }"
      :product-data="data ? { ...data, title: productsTitle } : null"
      @load-more="loadMore"
    />
    <cms-section
      v-if="!pending && eSpotLazyAboveFooter"
      :key="resultsCount"
      :class="brandClasses.eSpotLazyAboveFooter"
      lazy-media
      :section="eSpotLazyAboveFooter"
    />
  </main>
</template>

<script lang="ts" setup>
import type { VfSearchBar as VfSearchBarType } from '#components'
import type { ProductSearchQuery } from '#types/product'

defineOptions({ name: 'ProductSearchPage' })
definePageMeta({
  key: ({ query: { q } }) => `srp-${q}`,
  middleware: 'plp-store-filter',
  keepalive: true,
  pageTransition: false
})

const { getUserEventData } = useAnalytics()
const { productsPerPage, grid } = useAppConfig().components.product.list
const { brandClasses: tabsBrandClasses } = useAppConfig().components.vf.tabs
const { defaultSort, hideTitle } = useAppConfig().pages.plp
const {
  brandClasses,
  brandStyles,
  defaultSearchTerm,
  showAutoCorrect,
  showDidYouMean,
  showSearch
} = useAppConfig().pages.search
const cart = useCartStore()
const { getSections } = useCms()
const { injectFitFinder } = useFitFinder()
const { $dtrum, $t, $pluralize, $feature, $gtm, $viewport, $optimizely } = useNuxtApp()
const { trackEvent } = useOptimizely()
const route = useRoute()
const router = useRouter()
const filters = useRouteQuery('filters', '')
const searchQuery = useRouteQuery('q', '')
const sort = useRouteQuery('sort', defaultSort)
const storeFilter = $feature.showPickupFiltersOnSRP ? useRouteQuery<string | undefined>('storeFilter') : undefined
const activeTags = useRouteQuery<string[]>('tags', [], { transform: (param) => [param].flat() })
const { addSearch } = useSearchHistory()

const filtersQuery = computed(() => getFiltersQueryString(filters.value))
const query = reactive<ProductSearchQuery>({
  start: 0,
  count: productsPerPage,
  q: searchQuery.value || defaultSearchTerm || '',
  sort: sort.value,
  filters: filtersQuery.value,
  storeFilter: toValue(storeFilter),
  ...(route.query.customTrigger ? { customTrigger: route.query.customTrigger as string } : {})
})

const infinityLoad = ref(false)
const searchBarRef = ref<InstanceType<typeof VfSearchBarType>>()
const searchValue = ref(searchQuery.value || '')

const { data: articlesData, pending: articlesPending } = useAsyncData(
  `article-count-${searchQuery.value}`,
  () => useCms().getSearch('srp', {
    q: searchQuery.value,
    tagId: activeTags.value.join(','),
    countOnly: true
  }),
  {
    lazy: true,
    watch: [activeTags],
    server: false
  }
)

const { data, error, pending: productsPending } = useApi().products.search(query, {
  server: false,
  key: searchQuery.value,
  transform: (input): typeof input => ({
    ...input,
    did_you_mean: input.did_you_mean?.filter(Boolean) || [],
    filters: getUrlSafeFilters(input.filters),
    products: infinityLoad.value
      ? [
          ...(data.value?.products || []),
          ...input.products
        ]
      : input.products
  }),
  onResponse({ response: { _data } }) {
    const { keywordRedirect } = _data || {}

    if ($feature.enableSearchHistory)
      addSearch(searchQuery.value)

    if (keywordRedirect?.redirectedUrl) {
      const url = new URL(keywordRedirect.redirectedUrl, window.location.origin)
      const isSameHost = url.host === window.location.host
      const searchTermAdj = _data.autoCorrectQuery || _data.did_you_mean?.[0] || ''
      const searchType = history.state.searchType
      const state = {
        searchTerm: router.currentRoute.value.query.q as string || '',
        searchResults: _data.total || 0,
        searchTermAdj,
        searchType: searchType || (searchTermAdj ? 'Suggested search results' : 'Standard'),
        sourceId: _data.responseId,
        redirect: true
      }

      if (isSameHost) {
        router.replace({
          path: removeLocale(url.pathname),
          query: Object.fromEntries(url.searchParams.entries()),
          hash: url.hash,
          state
        })
      }

      else {
        navigateTo(removeLocale(keywordRedirect.redirectedUrl), {
          replace: true,
          external: /^http/i.test(keywordRedirect.redirectedUrl)
        })
      }
    }
  }
})

const didYouMeanSuggestions = computed(
  () => data.value?.did_you_mean && data.value.did_you_mean.length > 1
    ? data.value?.did_you_mean?.slice(1)
    : undefined
)
const isArticlesPage = computed(() => $feature.showArticlesTabOnSRP && route.name?.toString().includes('articles'))
const isRedirecting = computed(() => !!data.value?.keywordRedirect?.redirectedUrl)
const pending = computed(() => articlesPending.value || productsPending.value || isRedirecting.value)
const resultsCount = computed(() => (isArticlesPage.value ? articlesData.value?.numFound : data.value?.total) || 0)
const searchQueryAdjusted = computed(() => data.value?.autoCorrectQuery || data.value?.did_you_mean?.[0])

const articlesTitle = computed(
  () => articlesData.value?.numFound === 0
    ? replaceAll($t.searchResultsZero, { query: searchQueryAdjusted.value || searchQuery.value })
    : replaceAll(
        $pluralize($t.searchResultsTitle, articlesData.value?.numFound || 0),
        { query: (searchQueryAdjusted.value || searchQuery.value), count: articlesData.value?.numFound || 0 }
      )
)

const productsTitle = computed(
  () => !data.value?.total
    ? replaceAll($t.searchResultsZero, { query: searchQueryAdjusted.value || query.q })
    : replaceAll(
        $pluralize($t.searchResultsTitle, data.value?.total),
        { query: searchQueryAdjusted.value || query.q, count: data.value?.total }
      )
)

const autoCorrectMessage = computed(() =>
  showAutoCorrect && (data.value?.autoCorrectQuery || data.value?.did_you_mean?.[0])
    ? $t.searchResultsAutoCorrect
    : undefined
)

const didYouMeanMessage = computed(() =>
  showDidYouMean && data.value?.did_you_mean && data.value.did_you_mean.length > 1
    ? $t.searchResultsDidYouMean
    : undefined
)

const eSpots = $feature.enableSRPESpots ? (await getSections.search()).data.value?.content.sectionsMap : {}
const eSpotAboveGrid = computed(
  () => resultsCount.value > 0 ? eSpots?.['above-grid'] : eSpots?.['above-grid-no-results']
)
const eSpotLazyAboveFooter = computed(
  () => resultsCount.value > 0 ? eSpots?.['lazy-above-footer'] : eSpots?.['lazy-above-footer-no-results']
)

const trackingCategory = computed(() =>
  `Search: ${data.value?.autoCorrectQuery
    ? 'Auto-correction'
    : (window?.history.state.searchType || 'Standard')}`
)

const loadMore = () => {
  $gtm.push('plpPage.onViewMore')
  query.start! += query.count!
  infinityLoad.value = true
}

const pushImpressions = ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  if (!data.value?.products?.length) return

  const cols = { ...grid.cols, lg: $feature.showSideFiltersOnSRP ? grid.cols.lg - 1 : grid.cols.lg }
  let impressedProducts = data.value.products.map((product, index) => ({ ...product, index })) || []

  if (!allProductImpressions)
    impressedProducts = impressedProducts.slice(-data.value.items)

  const chunkSize = 24
  chunkArray(impressedProducts, chunkSize)
    .forEach((productsChunk, i) => {
      $gtm.push('product.onProductImpression', productsChunk, route.query.filters?.toString() || '', {
        sortOption: route.query.sort?.toString() || defaultSort,
        layout: cols[$viewport.breakpoint],
        searchTerm: query.q,
        category: trackingCategory.value,
        chunkId: i,
        offset: (query.start || 0) + i * chunkSize
      })
    })
}

const pushAllGtmEvents = async ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  // Whether the data is present immediately (SSR) or not (CSR), we check for its value
  await until(() => data.value).toBeTruthy()

  const searchTermAdj = data.value?.autoCorrectQuery || data.value?.did_you_mean?.[0] || ''
  const searchType = history.state.searchType

  $gtm.push('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    searchTerm: router.currentRoute.value.query.q as string || '',
    searchResults: data.value?.total || 0,
    searchTermAdj,
    searchType: searchType || (searchTermAdj ? 'Suggested search results' : 'Standard'),
    sourceId: data.value?.responseId
  })
  const userEventData = await getUserEventData()
  $gtm.push('user.onLoadUserData', userEventData)
  $gtm.push('page.onRouterChange', route)
  $gtm.push('cart.onCartLoad', cart)
  $optimizely.dataReady(userEventData)
  trackEvent('page_view', { URL: route?.fullPath })
  if (!isArticlesPage.value) pushImpressions({ allProductImpressions })
}

const submit = (value?: string) => {
  if (value && value !== query.q)
    router.push({ query: { ...route.query, q: value } })
}

useSeoMeta({
  title: () => replaceAll(
    $t.searchResultsPageTitle,
    { query: data.value?.autoCorrectQuery || searchQuery.value }
  ),
  robots: 'noindex, nofollow'
})

watch(() => [searchQuery.value, sort.value, storeFilter?.value, filters.value], (newValue, oldValue) => {
  if (newValue[0] !== oldValue?.[0]) return
  if (newValue.every((v, i) => v === oldValue?.[i])) return
  infinityLoad.value = false
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)
})

watchImmediate(searchQuery, () => {
  if (isClient() && $dtrum) {
    $dtrum.then((dtrum) => {
      const actionId = dtrum.enterAction('Searched')
      dtrum.addActionProperties(actionId, null, null, {
        searchTerm: searchQuery.value
      })
      dtrum.leaveAction(actionId)
    })
  }
})

onMounted(() => {
  history?.replaceState({
    category: trackingCategory.value,
    searchTerm: searchQuery.value,
    searchType: history.state.searchType
  }, '')
  pushAllGtmEvents()
  injectFitFinder()
  setInteractionOrigin()
  trackEvent('search', { searchTerm: searchQuery.value })
})

onActivated(async () => {
  if ($feature.enableSearchHistory)
    addSearch(searchQuery.value)

  // Push impressions after restoring from keep-alive cache
  await pushAllGtmEvents({ allProductImpressions: true })
  injectFitFinder()
})

onBeforeUnmount(() => data.value = null)
</script>
